/**
 * What a buyer sees: their purchase, the plan, what they have paid, and their statement.
 *
 * The person reading this has the longest relationship with the agency of anybody in the product —
 * three years of instalments is ordinary — and until now the portal mentioned purchases nowhere.
 *
 * **What is deliberately not on these screens**, because it is not in the answers either: anything
 * about another buyer, what else in the scheme sold for, how much of it is left, who recorded the
 * sale, or the agency's own notes on it. The API refuses all of it, and the screens must not imply
 * that any of it exists — a heading with an empty panel under it is a promise that there is more to
 * see. ADR-0102.
 *
 * **Money arrives already written out.** Every figure comes back with a `…Label` beside its integer
 * count of minor units, formatted by the API in the agency's own currency. The screen prints the
 * label and never does arithmetic on the number, so a buyer and their agency cannot be looking at
 * two different totals.
 */

import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { formatDate } from "@rentorng/core";
import { Alert, Badge, Button, Card, CardTitle, Detail, EmptyState, Spinner, Table } from "@/components/ui";
import { IconEstate, IconPlot, IconStatement } from "@/components/icons";
import { AgencyMark } from "@/components/agency-mark";
import { usePageTitle } from "@/lib/title";
import { PortalProvider, usePortal, type PortalMe } from "@/lib/portal";
import { PortalShell } from "./PortalShell";

/* ------------------------------------------------------------------ the shapes */

export interface PurchaseSummary {
  id: string;
  reference: string;
  status: string;
  price: number;
  priceLabel: string;
  startDate: string;
  handedOverOn: string | null;
  estateName: string;
  estateCity: string | null;
  plotLabel: string | null;
  outstanding: number;
  outstandingLabel: string;
  arrears: number;
  arrearsLabel: string;
  percentPaid: number;
  nextDue: { sequence: number; dueDate: string; amount: number } | null;
}

interface Instalment {
  id: string;
  sequence: number;
  label: string | null;
  dueDate: string;
  amount: number;
  paid: number;
  lateFee: number;
  status: "due" | "part_paid" | "paid" | "overdue" | "waived";
  amountLabel: string;
  paidLabel: string;
}

interface Receipt {
  id: string;
  receiptNumber: string;
  amount: number;
  amountLabel: string;
  method: string;
  status: string;
  receivedOn: string;
  reference: string | null;
}

interface PurchaseDetail {
  purchase: {
    id: string;
    reference: string;
    status: string;
    priceLabel: string;
    depositLabel: string;
    frequency: string;
    instalments: number;
    startDate: string;
    allocatedAt: string | null;
    handedOverOn: string | null;
  };
  estate: { id: string; name: string; city: string | null; titleType: string | null };
  plot: { id: string; label: string | null; sizeSqm: number | null } | null;
  instalments: Instalment[];
  receipts: Receipt[];
  standing: {
    percentPaid: number;
    settled: boolean;
    nextDue: { sequence: number; dueDate: string; amount: number } | null;
    priceLabel: string;
    billedLabel: string;
    paidLabel: string;
    outstandingLabel: string;
    arrearsLabel: string;
    creditLabel: string;
    arrears: number;
    credit: number;
    outstanding: number;
  };
  settlement: { outstandingLabel: string; discountLabel: string; payableLabel: string; discount: number };
  asAt: string;
  /** Only on the statement. */
  buyer?: { name: string };
  agency?: { name: string; phone: string | null; email: string | null; logoUrl?: string | null } | null;
}

/* --------------------------------------------------------------- small pieces */

/** What one instalment row is, in a word the reader already uses. */
function InstalmentState({ status }: { status: Instalment["status"] }) {
  if (status === "paid") return <Badge tone="success">Paid</Badge>;
  if (status === "overdue") return <Badge tone="danger">Overdue</Badge>;
  if (status === "part_paid") return <Badge tone="warn">Part paid</Badge>;
  if (status === "waived") return <Badge tone="muted">Waived</Badge>;
  return <Badge tone="muted">Due</Badge>;
}

/** How far through the plan, drawn rather than described. */
function Progress({ percent }: { percent: number }) {
  return (
    <div className="mt-3">
      <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
        <div className="h-full rounded-full bg-brand-500" style={{ width: `${Math.max(2, percent)}%` }} />
      </div>
      <p className="mt-1.5 text-xs text-ink-500">{percent}% of the price paid so far</p>
    </div>
  );
}

/**
 * The buyer's purchases on the account screen.
 *
 * A card each rather than a table: somebody typically has one, and a one-row table with five
 * columns of headings is a worse way to read one thing than a card is.
 */
export function PurchaseSummaries({ purchases }: { purchases: PurchaseSummary[] }) {
  return (
    <div className="mb-5 space-y-4">
      {purchases.map((purchase) => (
        <Card key={purchase.id}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <IconEstate className="size-5 text-ink-400" />
                {purchase.estateName}
              </h2>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-ink-500">
                {purchase.plotLabel ? (
                  <span className="inline-flex items-center gap-1">
                    <IconPlot className="size-4" />
                    {purchase.plotLabel}
                  </span>
                ) : null}
                {purchase.estateCity ? <span>{purchase.estateCity}</span> : null}
                <span>{purchase.reference}</span>
              </p>
            </div>
            <Link to={`/portal/purchases/${purchase.id}`} className="btn btn-secondary btn-sm">
              Open
            </Link>
          </div>

          <dl className="mt-4 grid gap-x-6 sm:grid-cols-2">
            <Detail label="Price">{purchase.priceLabel}</Detail>
            <Detail label="Left to pay">
              <span className={purchase.outstanding > 0 ? "text-ink-900" : "text-success-700"}>
                {purchase.outstanding > 0 ? purchase.outstandingLabel : "Nothing"}
              </span>
            </Detail>
            {/* Only when there is something behind. A row saying "nothing overdue" on every screen
                trains people to stop reading the row. */}
            {purchase.arrears > 0 ? (
              <Detail label="Overdue">
                <span className="text-danger-700">{purchase.arrearsLabel}</span>
              </Detail>
            ) : null}
            {purchase.nextDue ? (
              <Detail label="Next payment">{formatDate(purchase.nextDue.dueDate)}</Detail>
            ) : purchase.handedOverOn ? (
              <Detail label="Handed over">{formatDate(purchase.handedOverOn)}</Detail>
            ) : null}
          </dl>

          <Progress percent={purchase.percentPaid} />
        </Card>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------- one purchase */

/** Load one purchase, or the same thing dressed as a statement. Both answers have the same shape. */
function usePurchase(path: string) {
  const { get, loading, me } = usePortal();
  const [data, setData] = useState<PurchaseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || !me) return;
    let cancelled = false;
    get<PurchaseDetail>(path)
      .then((value) => {
        if (!cancelled) setData(value);
      })
      .catch((problem: unknown) => {
        if (!cancelled) {
          setError(problem instanceof Error ? problem.message : "That could not be opened.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [get, path, loading, me]);

  return { data, error, loading, me };
}

function InstalmentTable({ instalments }: { instalments: Instalment[] }) {
  return (
    <Table
      head={
        <tr>
          <th>Payment</th>
          <th>Due</th>
          <th className="num">Amount</th>
          <th className="num">Paid</th>
          <th>State</th>
        </tr>
      }
    >
      {instalments.map((row) => (
        <tr key={row.id}>
          <td className="font-medium">{row.label || `Instalment ${row.sequence}`}</td>
          <td>{formatDate(row.dueDate)}</td>
          <td className="num tabular">{row.amountLabel}</td>
          <td className="num tabular">{row.paid > 0 ? row.paidLabel : "—"}</td>
          <td>
            <InstalmentState status={row.status} />
          </td>
        </tr>
      ))}
    </Table>
  );
}

function ReceiptTable({ receipts }: { receipts: Receipt[] }) {
  if (!receipts.length) return <EmptyState title="No payments have been recorded against this purchase yet." />;
  return (
    <Table
      head={
        <tr>
          <th>Receipt</th>
          <th>Received</th>
          <th>How</th>
          <th className="num">Amount</th>
        </tr>
      }
    >
      {receipts.map((receipt) => (
        <tr key={receipt.id}>
          <td className="font-medium">{receipt.receiptNumber}</td>
          <td>{formatDate(receipt.receivedOn)}</td>
          <td className="capitalize">{receipt.method.replace(/_/g, " ")}</td>
          <td className="num tabular">{receipt.amountLabel}</td>
        </tr>
      ))}
    </Table>
  );
}

/** Signed out, still loading, or an id that is not theirs — the three ways this screen has nothing to draw. */
function PurchaseGate({ error, loading, me }: { error: string | null; loading: boolean; me: PortalMe | null }) {
  /* Not an error. Somebody whose session has expired, which happens on a screen that is bookmarked
     and opened months later — which is exactly what a three-year payment plan invites. */
  if (!loading && !me) return <Navigate to="/portal/sign-in" replace />;

  if (error) {
    return (
      <PortalShell agency={me?.agency}>
        <Card>
          <h1 className="text-xl font-semibold">Not found</h1>
          <div className="mt-3">
            <Alert tone="danger">{error}</Alert>
          </div>
          <Link to="/portal" className="btn btn-secondary mt-5 w-full">
            Back to your account
          </Link>
        </Card>
      </PortalShell>
    );
  }

  return (
    <PortalShell agency={me?.agency}>
      <div className="flex items-center gap-3">
        <Spinner />
        <p className="text-sm text-ink-500">Loading…</p>
      </div>
    </PortalShell>
  );
}

export function PortalPurchase() {
  const { id } = useParams();
  const { data, error, loading, me } = usePurchase(`/purchases/${id}`);
  usePageTitle(data ? `${data.estate.name} · Your purchase` : "Your purchase");

  if (!data) return <PurchaseGate error={error} loading={loading} me={me} />;

  const { purchase, estate, plot, standing, settlement } = data;

  return (
    <PortalShell agency={me?.agency}>
      <div className="mb-5">
        <Link to="/portal" className="link text-sm">
          ← Your account
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">{estate.name}</h1>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-ink-500">
          {plot?.label ? <span>{plot.label}</span> : null}
          {estate.city ? <span>· {estate.city}</span> : null}
          <span>· {purchase.reference}</span>
        </p>
      </div>

      {/* The question somebody opens this screen to answer, before anything they have to read. */}
      <Card className="mb-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Left to pay</p>
        <p className={`tabular mt-1 text-3xl font-semibold ${standing.outstanding > 0 ? "text-ink-900" : "text-success-700"}`}>
          {standing.outstanding > 0 ? standing.outstandingLabel : "Nothing"}
        </p>
        {standing.arrears > 0 ? (
          <p className="mt-1 text-sm font-medium text-danger-700">{standing.arrearsLabel} of that is past its date.</p>
        ) : standing.nextDue ? (
          <p className="mt-1 text-sm text-ink-500">Next payment is due on {formatDate(standing.nextDue.dueDate)}.</p>
        ) : (
          <p className="mt-1 text-sm text-ink-500">Every payment on this plan has been made.</p>
        )}
        <Progress percent={standing.percentPaid} />

        <div className="mt-4 flex flex-wrap gap-2">
          <Link to={`/portal/purchases/${purchase.id}/statement`} className="btn btn-secondary btn-sm">
            <span className="inline-flex items-center gap-1.5">
              <IconStatement className="size-4" /> Your statement
            </span>
          </Link>
        </div>
      </Card>

      <div className="mb-5 grid gap-5 sm:grid-cols-2">
        <Card>
          <CardTitle>What you agreed</CardTitle>
          <dl>
            <Detail label="Price">{purchase.priceLabel}</Detail>
            <Detail label="Deposit">{purchase.depositLabel}</Detail>
            <Detail label="Payments">
              {purchase.instalments} <span className="capitalize">{purchase.frequency.replace(/_/g, " ")}</span>
            </Detail>
            <Detail label="Started">{formatDate(purchase.startDate)}</Detail>
            {plot?.sizeSqm ? <Detail label="Size">{plot.sizeSqm} sqm</Detail> : null}
            {estate.titleType ? <Detail label="Title">{estate.titleType}</Detail> : null}
            {purchase.handedOverOn ? <Detail label="Handed over">{formatDate(purchase.handedOverOn)}</Detail> : null}
          </dl>
        </Card>

        <Card>
          <CardTitle>Where it stands</CardTitle>
          <dl>
            <Detail label="Paid so far">{standing.paidLabel}</Detail>
            <Detail label="Billed to date">{standing.billedLabel}</Detail>
            <Detail label="Left to pay">{standing.outstandingLabel}</Detail>
            {standing.credit > 0 ? <Detail label="Held as credit">{standing.creditLabel}</Detail> : null}
            {/* Only where there is a discount to have. Otherwise this reads as an offer that was
                refused, when in fact none was ever agreed. */}
            {settlement.discount > 0 ? (
              <Detail label="Settle it all today">
                {settlement.payableLabel}{" "}
                <span className="text-xs font-normal text-ink-500">after {settlement.discountLabel} off</span>
              </Detail>
            ) : null}
          </dl>
        </Card>
      </div>

      <Card className="mb-5" padded={false}>
        <div className="p-4 sm:p-5">
          <CardTitle hint="Every payment on the plan, and where each one has got to.">Your payment plan</CardTitle>
        </div>
        <InstalmentTable instalments={data.instalments} />
      </Card>

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardTitle hint="What the agency has recorded as received from you.">Receipts</CardTitle>
        </div>
        <ReceiptTable receipts={data.receipts} />
      </Card>
    </PortalShell>
  );
}

/* ------------------------------------------------------------------ the statement */

/**
 * The statement, as a buyer would print or send it.
 *
 * The same figures as the screen above with the agency's name and contact details on top, laid out
 * to be read on paper by somebody who was not in the room. `printable` puts it alone on the sheet;
 * everything else on the page carries `no-print`.
 */
export function PortalPurchaseStatement() {
  const { id } = useParams();
  const { data, error, loading, me } = usePurchase(`/purchases/${id}/statement`);
  usePageTitle(data ? `Statement · ${data.estate.name}` : "Your statement");

  if (!data) return <PurchaseGate error={error} loading={loading} me={me} />;

  const { purchase, estate, plot, standing, agency, buyer } = data;

  return (
    <PortalShell agency={me?.agency}>
      <div className="no-print mb-5 flex flex-wrap items-center justify-between gap-3">
        <Link to={`/portal/purchases/${purchase.id}`} className="link text-sm">
          ← Back to the purchase
        </Link>
        <Button variant="secondary" size="sm" onClick={() => window.print()}>
          Print or save
        </Button>
      </div>

      <Card className="printable" padded={false}>
        <div className="border-b border-ink-200 p-5">
          {/*
            The agency's letterhead leads and the document names itself opposite it — the same way
            round as the invoice, the receipt and the landlord's statement in the agency's own
            application, and through the same component, so the four cannot drift apart. It used to
            be the other way round, with the agency set small and grey on the right, which is not
            how a document that goes out under somebody's name is headed.
          */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            {agency ? (
              <AgencyMark name={agency.name} logoUrl={agency.logoUrl} size="compact">
                {agency.phone ? <p className="text-xs text-ink-500">{agency.phone}</p> : null}
                {agency.email ? <p className="text-xs text-ink-500">{agency.email}</p> : null}
              </AgencyMark>
            ) : (
              <div />
            )}
            <div className="sm:text-right">
              <h1 className="text-xl font-semibold">Statement of account</h1>
              <p className="mt-1 text-sm text-ink-500">
                {estate.name}
                {plot?.label ? ` · ${plot.label}` : ""} · {purchase.reference}
              </p>
              <p className="mt-0.5 text-sm text-ink-500">As at {formatDate(data.asAt)}</p>
            </div>
          </div>
          {buyer ? <p className="mt-4 text-sm text-ink-700">For {buyer.name}</p> : null}
        </div>

        <div className="grid gap-x-8 border-b border-ink-200 p-5 sm:grid-cols-2">
          <dl>
            <Detail label="Price agreed">{standing.priceLabel}</Detail>
            <Detail label="Billed to date">{standing.billedLabel}</Detail>
            <Detail label="Received">{standing.paidLabel}</Detail>
          </dl>
          <dl>
            <Detail label="Left to pay">{standing.outstandingLabel}</Detail>
            {standing.arrears > 0 ? <Detail label="Past its date">{standing.arrearsLabel}</Detail> : null}
            {standing.credit > 0 ? <Detail label="Held as credit">{standing.creditLabel}</Detail> : null}
          </dl>
        </div>

        <div className="p-5 pb-2">
          <CardTitle>The payment plan</CardTitle>
        </div>
        <InstalmentTable instalments={data.instalments} />

        <div className="p-5 pb-2">
          <CardTitle>Payments received</CardTitle>
        </div>
        <ReceiptTable receipts={data.receipts} />

        {agency ? (
          <p className="p-5 text-sm text-ink-500">
            Anything that looks wrong, speak to {agency.name}
            {agency.phone ? ` on ${agency.phone}` : ""}
            {agency.email ? ` or at ${agency.email}` : ""}.
          </p>
        ) : null}
      </Card>
    </PortalShell>
  );
}

/* --------------------------------------------------------------------- the routes */

export function PortalPurchasePage() {
  return (
    <PortalProvider>
      <PortalPurchase />
    </PortalProvider>
  );
}

export function PortalPurchaseStatementPage() {
  return (
    <PortalProvider>
      <PortalPurchaseStatement />
    </PortalProvider>
  );
}
